import {
  Card,
  Checkbox,
  CrudMode,
  DataDisplay,
  Grid,
  Stack,
  ValidationErrors,
  formatBoolean,
  formatPhoneNumber,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { Input } from "components";
import { Contributor, ContributorViewResource } from "generated/openApi";

import produce from "immer";

export const ContributorBasicInfo = (props: {
  contributor: Contributor;
  setContributor: (value: Contributor) => void;
  viewResource: ContributorViewResource;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const lastNameField = () => {
    const propertyName = pn<Contributor>("lastName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.contributor.lastName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.contributor.lastName}
        onChange={(e) =>
          props.setContributor(
            produce(props.contributor, (draft) => {
              draft.lastName = e.target.value;
            })
          )
        }
      />
    );
  };

  const firstNameField = () => {
    const propertyName = pn<Contributor>("firstName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.contributor.firstName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.contributor.firstName}
        onChange={(e) =>
          props.setContributor(
            produce(props.contributor, (draft) => {
              draft.firstName = e.target.value;
            })
          )
        }
      />
    );
  };

  const employeeIdentificationField = () => {
    const propertyName = pn<Contributor>("employeeIdentification");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.contributor.employeeIdentification}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.contributor.employeeIdentification}
        onChange={(e) =>
          props.setContributor(
            produce(props.contributor, (draft) => {
              draft.employeeIdentification = e.target.value;
            })
          )
        }
      />
    );
  };

  const titleField = () => {
    const propertyName = pn<Contributor>("currentTitle");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Grid item xs={smallFieldBreakpoints}>
        <DataDisplay label={property?.displayName}>{props.contributor.currentTitle}</DataDisplay>
      </Grid>
    );
  };

  const emailField = () => {
    const propertyName = pn<Contributor>("email");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.contributor.email}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.contributor.email}
        onChange={(e) =>
          props.setContributor(
            produce(props.contributor, (draft) => {
              draft.email = e.target.value;
            })
          )
        }
      />
    );
  };

  const phoneField = () => {
    const propertyName = pn<Contributor>("phone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label="Phone">{formatPhoneNumber(props.contributor.phone)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.contributor.phone}
        onChange={(e) =>
          props.setContributor(
            produce(props.contributor, (draft) => {
              draft.phone = e.target.value;
            })
          )
        }
      />
    );
  };

  const phoneExtensionField = () => {
    const propertyName = pn<Contributor>("phoneExtension");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.contributor.phoneExtension}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.contributor.phoneExtension}
        onChange={(e) =>
          props.setContributor(
            produce(props.contributor, (draft) => {
              draft.phoneExtension = e.target.value;
            })
          )
        }
      />
    );
  };

  const faxField = () => {
    const propertyName = pn<Contributor>("fax");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatPhoneNumber(props.contributor.fax)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[pn<Contributor>("fax")]}
        value={props.contributor.fax}
        onChange={(e) =>
          props.setContributor(
            produce(props.contributor, (draft) => {
              draft.fax = e.target.value;
            })
          )
        }
      />
    );
  };

  const federalField = () => {
    const propertyName = pn<Contributor>("federal");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.contributor.federal)}</DataDisplay>
    ) : (
      <Stack direction="row" alignItems="center" height="100%">
        <Checkbox
          label={property?.displayName}
          checked={props.contributor.federal}
          onChange={(e) =>
            props.setContributor(
              produce(props.contributor, (draft) => {
                draft.federal = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  const noteField = () => {
    const propertyName = pn<Contributor>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.contributor.note}
      </DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        multiline
        value={props.contributor.note}
        onChange={(e) =>
          props.setContributor(
            produce(props.contributor, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Card header={"Info"}>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {lastNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {firstNameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {employeeIdentificationField()}
        </Grid>

        {props.contributor && props.contributor.id > 0 && (
          <Grid item xs={smallFieldBreakpoints}>
            {titleField()}
          </Grid>
        )}

        <Grid item xs={smallFieldBreakpoints}>
          {emailField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          <Stack direction="row" spacing="sm">
            <Stack flexBasis={"70%"}>{phoneField()}</Stack>
            <Stack flexBasis={"30%"}>{phoneExtensionField()}</Stack>
          </Stack>
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {faxField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {federalField()}
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          {noteField()}
        </Grid>
      </Grid>
    </Card>
  );
};
